import { useMutation, useQueryClient } from "@tanstack/react-query";
import { signInWithEmailAndPassword } from "firebase/auth";

import { auth, forceInMemorySession } from "@/lib/firebase/firebase-client";

import { CROSS_TENANT_QUERY_KEY_ROOTS } from "@/dashboard/constants/cross-tenant-query-keys";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { HttpError } from "@/dashboard/lib/api/errors";

interface SignInArgs {
  email: string;
  password: string;
  googleIdpTenantId: string;
}

export function useSignIn() {
  const qc = useQueryClient();

  return useMutation<void, Error, SignInArgs>({
    mutationFn: async ({ email, password, googleIdpTenantId }) => {
      const previousTenant = auth.tenantId;
      auth.tenantId = googleIdpTenantId;

      try {
        await forceInMemorySession();

        const { user } = await signInWithEmailAndPassword(auth, email, password);
        const idToken = await user.getIdToken();

        await fetchJson("/api/dashboard/session", {
          method: "POST",
          body: JSON.stringify({ idToken }),
        });

        // Verify the user actually exists in our DB (Firebase user may
        // outlive a deleted account). Only treat a 404 as "user not found";
        // let transient failures (network errors, 500s) propagate as-is.
        try {
          await fetchJson("/api/dashboard/session", { cache: "no-store" });
        } catch (err) {
          if (err instanceof HttpError && err.status === 404) {
            throw new Error("auth/user-not-found");
          }
          throw err;
        }
      } finally {
        auth.tenantId = previousTenant;
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      qc.removeQueries({ queryKey: ["call-source-status"] });
      // User change must evict cross-tenant keys (they survive tenant switches).
      for (const key of CROSS_TENANT_QUERY_KEY_ROOTS) {
        qc.removeQueries({ queryKey: [key] });
      }
    },
  });
}
